import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Award, Lightbulb, History, RefreshCw, Check, X, Plus, ClipboardList, FileDown, Trash2, Mail, Crown, Send } from "lucide-react";
import "./employee.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("dtile_admin_token")}` } });

const PRIORITY_COLORS = { low: "Silver", normal: "Bronze", high: "Gold", urgent: "NeedsImprovement" };
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];


export function AttendanceAdminPanel() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [emps, setEmps] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: att }, { data: e }] = await Promise.all([
        axios.get(`${API}/admin/attendance?days=${days}`, auth()),
        axios.get(`${API}/admin/employees`, auth()),
      ]);
      setRows(att || []); setEmps(e || []);
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const empMap = Object.fromEntries(emps.map(e => [e.id, e]));

  return (
    <div className="emp-scope emp-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="emp-eyebrow inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Attendance · last {days} days</p>
        <div className="flex items-center gap-2">
          <select className="emp-input" style={{ width: 100 }} value={days} onChange={e => setDays(+e.target.value)} data-testid="att-days">
            <option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={60}>60 days</option>
          </select>
          <button onClick={load} className="emp-btn-ghost inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Refresh</button>
        </div>
      </div>
      <div className="emp-glass overflow-x-auto">
        <table className="w-full text-sm" data-testid="att-table">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--emp-border)" }}>
              <th className="p-3 emp-eyebrow">Date</th>
              <th className="p-3 emp-eyebrow">Employee</th>
              <th className="p-3 emp-eyebrow">Clock In</th>
              <th className="p-3 emp-eyebrow">Clock Out</th>
              <th className="p-3 emp-eyebrow">Minutes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="p-8 text-center emp-eyebrow">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: "var(--emp-muted)" }}>No attendance records in this range.</td></tr>
              : rows.map(r => {
                const e = empMap[r.employee_id];
                return (
                  <tr key={r.id} className="border-b" style={{ borderColor: "var(--emp-border)" }} data-testid={`att-row-${r.id}`}>
                    <td className="p-3">{r.date}</td>
                    <td className="p-3">{e ? `${e.name} (${e.employee_code})` : r.employee_id}</td>
                    <td className="p-3">{r.clock_in_at ? new Date(r.clock_in_at).toLocaleTimeString() : "—"}</td>
                    <td className="p-3">{r.clock_out_at ? new Date(r.clock_out_at).toLocaleTimeString() : "—"}</td>
                    <td className="p-3">{r.minutes || "—"}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export function BehaviourAdminPanel() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);
  const [emps, setEmps] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", kind: "commendation", delta: 10, reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: e }] = await Promise.all([
        axios.get(`${API}/admin/behaviour-events?kind=${kind}`, auth()),
        axios.get(`${API}/admin/employees`, auth()),
      ]);
      setRows(b || []); setEmps(e || []);
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const submit = async () => {
    if (!form.employee_id || !form.reason.trim()) { toast.error("Employee + reason required"); return; }
    try {
      await axios.post(`${API}/admin/behaviour-events`, form, auth());
      toast.success("Behaviour event logged"); setAddOpen(false);
      setForm({ employee_id: "", kind: "commendation", delta: 10, reason: "" }); load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="emp-scope emp-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="emp-eyebrow inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5" strokeWidth={1.5} /> Behaviour Ledger</p>
        <div className="flex items-center gap-2">
          <select className="emp-input" style={{ width: 160 }} value={kind} onChange={e => setKind(e.target.value)} data-testid="beh-kind">
            <option value="all">All kinds</option>
            <option value="commendation">Commendations</option>
            <option value="note">Notes</option>
            <option value="warning">Warnings</option>
          </select>
          <button onClick={() => setAddOpen(o => !o)} className="emp-btn-primary inline-flex items-center gap-1" data-testid="beh-add-toggle"><Plus className="w-3 h-3" strokeWidth={1.5} /> New Event</button>
        </div>
      </div>

      {addOpen && (
        <div className="emp-glass p-5 mb-4" data-testid="beh-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <select className="emp-input" value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} data-testid="beh-emp">
              <option value="">Select employee…</option>
              {emps.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
            </select>
            <select className="emp-input" value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value, delta: e.target.value === "warning" ? -10 : e.target.value === "commendation" ? 10 : 0 }))}>
              <option value="commendation">Commendation</option>
              <option value="note">Note</option>
              <option value="warning">Warning</option>
            </select>
            <input type="number" className="emp-input" placeholder="Delta ±" value={form.delta} onChange={e => setForm(f => ({ ...f, delta: +e.target.value }))} data-testid="beh-delta" />
            <input className="emp-input" placeholder="Reason (required)" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} data-testid="beh-reason" />
          </div>
          <div className="mt-3 text-right">
            <button onClick={submit} className="emp-btn-primary" data-testid="beh-submit">Log Event</button>
          </div>
        </div>
      )}

      <div className="emp-glass overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--emp-border)" }}>
              <th className="p-3 emp-eyebrow">When</th>
              <th className="p-3 emp-eyebrow">Employee</th>
              <th className="p-3 emp-eyebrow">Kind</th>
              <th className="p-3 emp-eyebrow">Δ</th>
              <th className="p-3 emp-eyebrow">Reason</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="p-8 text-center emp-eyebrow">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: "var(--emp-muted)" }}>No behaviour events yet.</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b" style={{ borderColor: "var(--emp-border)" }} data-testid={`beh-row-${r.id}`}>
                  <td className="p-3 text-xs">{new Date(r.at).toLocaleString()}</td>
                  <td className="p-3">{r.employee_name || r.employee_id} <span className="emp-eyebrow" style={{ opacity: 0.5 }}>{r.employee_code}</span></td>
                  <td className="p-3"><span className={`emp-grade emp-grade-${r.kind === "commendation" ? "Gold" : r.kind === "warning" ? "NeedsImprovement" : "Silver"}`}>{r.kind}</span></td>
                  <td className="p-3 font-mono" style={{ color: r.delta >= 0 ? "#10B981" : "#EF4444" }}>{r.delta >= 0 ? "+" : ""}{r.delta}</td>
                  <td className="p-3 text-sm">{r.reason}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export function InnovationAdminPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState({}); // {id: {points, note}}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/innovations?status=${status}`, auth());
      setRows(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (id, approve) => {
    const d = decisions[id] || {};
    try {
      await axios.patch(`${API}/admin/innovations/${id}/decide`, {
        approve, points: approve ? (d.points || 20) : 0, reason: d.note || null,
      }, auth());
      toast.success(approve ? `Approved · +${d.points || 20} innovation` : "Rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="emp-scope emp-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="emp-eyebrow inline-flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5} /> Innovation Queue</p>
        <select className="emp-input" style={{ width: 160 }} value={status} onChange={e => setStatus(e.target.value)} data-testid="inn-status">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? <p className="text-center emp-eyebrow py-8">Loading…</p>
        : rows.length === 0 ? <p className="emp-glass p-8 text-center text-sm" style={{ color: "var(--emp-muted)" }}>Nothing to review here.</p>
        : <div className="space-y-4">
          {rows.map(r => (
            <div key={r.id} className="emp-glass p-5" data-testid={`inn-card-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-editorial text-lg">{r.title}</p>
                  <p className="emp-eyebrow mt-1" style={{ opacity: 0.6 }}>{r.employee_code} · {new Date(r.submitted_at).toLocaleString()}</p>
                  <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: "var(--emp-muted)" }}>{r.description}</p>
                  {r.admin_note && <p className="text-xs mt-3 italic" style={{ color: "var(--emp-muted)" }}>Admin: {r.admin_note}</p>}
                </div>
                {r.status === "pending" ? (
                  <div className="flex flex-col items-end gap-2 w-56 flex-shrink-0">
                    <input type="number" min={0} max={50} placeholder="Points (default 20)"
                      className="emp-input" data-testid={`inn-pts-${r.id}`}
                      onChange={e => setDecisions(d => ({ ...d, [r.id]: { ...d[r.id], points: +e.target.value } }))} />
                    <input placeholder="Note (optional)" className="emp-input" data-testid={`inn-note-${r.id}`}
                      onChange={e => setDecisions(d => ({ ...d, [r.id]: { ...d[r.id], note: e.target.value } }))} />
                    <div className="flex gap-2 w-full">
                      <button onClick={() => decide(r.id, true)} className="emp-btn-primary flex-1 inline-flex items-center justify-center gap-1" data-testid={`inn-approve-${r.id}`}><Check className="w-3 h-3" strokeWidth={1.5} /> Approve</button>
                      <button onClick={() => decide(r.id, false)} className="emp-btn-ghost flex-1 inline-flex items-center justify-center gap-1" style={{ borderColor: "#EF4444", color: "#EF4444" }} data-testid={`inn-reject-${r.id}`}><X className="w-3 h-3" strokeWidth={1.5} /> Reject</button>
                    </div>
                  </div>
                ) : (
                  <span className={`emp-grade emp-grade-${r.status === "approved" ? "Gold" : "NeedsImprovement"}`}>{r.status}{r.status === "approved" && ` +${r.points_awarded}`}</span>
                )}
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}


export function LoginHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?event=${filter}`;
      const { data } = await axios.get(`${API}/admin/audit${params}`, auth());
      setRows(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="emp-scope emp-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="emp-eyebrow inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" strokeWidth={1.5} /> Login History & Audit</p>
        <select className="emp-input" style={{ width: 200 }} value={filter} onChange={e => setFilter(e.target.value)} data-testid="audit-filter">
          <option value="all">All events</option>
          <option value="employee.login">Logins</option>
          <option value="employee.logout">Logouts</option>
          <option value="employee.password_changed">Password changes</option>
          <option value="employee.updated">Profile updates</option>
        </select>
      </div>
      <div className="emp-glass overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--emp-border)" }}>
              <th className="p-3 emp-eyebrow">When</th>
              <th className="p-3 emp-eyebrow">Event</th>
              <th className="p-3 emp-eyebrow">Employee</th>
              <th className="p-3 emp-eyebrow">Actor</th>
              <th className="p-3 emp-eyebrow">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="p-8 text-center emp-eyebrow">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: "var(--emp-muted)" }}>No events.</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b" style={{ borderColor: "var(--emp-border)" }}>
                  <td className="p-3 text-xs">{new Date(r.at).toLocaleString()}</td>
                  <td className="p-3 font-mono text-xs">{r.event}</td>
                  <td className="p-3">{r.employee_name || "—"} {r.employee_code && <span className="emp-eyebrow" style={{ opacity: 0.5 }}>{r.employee_code}</span>}</td>
                  <td className="p-3 text-xs">{r.actor}</td>
                  <td className="p-3 text-[11px]" style={{ color: "var(--emp-muted)" }}>
                    {r.meta?.reason || r.meta?.bucket || (r.meta?.fields ? r.meta.fields.join(", ") : "—")}
                    {typeof r.meta?.delta === "number" && <span className="ml-1 font-mono" style={{ color: r.meta.delta >= 0 ? "#10B981" : "#EF4444" }}>({r.meta.delta >= 0 ? "+" : ""}{r.meta.delta})</span>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ============================================================
// Shipment B · Tasks Admin Panel
// ============================================================
export function TasksAdminPanel() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", title: "", description: "", priority: "normal", due_date: "" });
  const [loading, setLoading] = useState(true);

  const activeEmps = useMemo(
    () => emps.filter(e => e.employment_status === "Active"),
    [emps]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: e }] = await Promise.all([
        axios.get(`${API}/admin/employee-tasks?status=${statusFilter}`, auth()),
        axios.get(`${API}/admin/employees`, auth()),
      ]);
      setRows(t || []); setEmps(e || []);
    } catch (er) { toast.error(er.response?.data?.detail || "Failed to load tasks"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const submit = async () => {
    if (!form.employee_id || !form.title.trim()) { toast.error("Employee + title required"); return; }
    try {
      await axios.post(`${API}/admin/employee-tasks`, form, auth());
      toast.success("Task assigned");
      setAddOpen(false); setForm({ employee_id: "", title: "", description: "", priority: "normal", due_date: "" });
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await axios.delete(`${API}/admin/employee-tasks/${id}`, auth()); toast.success("Deleted"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="emp-scope emp-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="emp-eyebrow inline-flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" strokeWidth={1.5} /> Today's Tasks · assign & track</p>
        <div className="flex items-center gap-2">
          <select className="emp-input" style={{ width: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="task-status-filter">
            <option value="pending">Pending</option>
            <option value="done">Completed</option>
            <option value="all">All</option>
          </select>
          <button onClick={() => setAddOpen(o => !o)} className="emp-btn-primary inline-flex items-center gap-1" data-testid="task-add-toggle">
            <Plus className="w-3 h-3" strokeWidth={1.5} /> Assign Task
          </button>
        </div>
      </div>

      {addOpen && (
        <div className="emp-glass p-5 mb-4" data-testid="task-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select className="emp-input" value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} data-testid="task-emp">
              <option value="">Select employee…</option>
              {activeEmps.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
            </select>
            <select className="emp-input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} data-testid="task-priority">
              <option value="low">Low</option>
              <option value="normal">Normal (+2 pts)</option>
              <option value="high">High (+4 pts)</option>
              <option value="urgent">Urgent (+6 pts)</option>
            </select>
            <input className="emp-input sm:col-span-2" placeholder="Task title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="task-title" />
            <textarea rows={2} className="emp-input sm:col-span-2 resize-none" placeholder="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="task-desc" />
            <input type="date" className="emp-input" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due" />
          </div>
          <div className="mt-3 text-right">
            <button onClick={submit} className="emp-btn-primary" data-testid="task-submit">Assign</button>
          </div>
        </div>
      )}

      <div className="emp-glass overflow-x-auto">
        <table className="w-full text-sm" data-testid="task-table">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--emp-border)" }}>
              <th className="p-3 emp-eyebrow">Priority</th>
              <th className="p-3 emp-eyebrow">Task</th>
              <th className="p-3 emp-eyebrow">Employee</th>
              <th className="p-3 emp-eyebrow">Due</th>
              <th className="p-3 emp-eyebrow">Status</th>
              <th className="p-3 emp-eyebrow" style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-8 text-center emp-eyebrow">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-sm" style={{ color: "var(--emp-muted)" }}>No tasks in this view.</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b" style={{ borderColor: "var(--emp-border)" }} data-testid={`task-row-${r.id}`}>
                  <td className="p-3"><span className={`emp-grade emp-grade-${PRIORITY_COLORS[r.priority] || "Bronze"}`}>{r.priority}</span></td>
                  <td className="p-3"><p className="font-medium">{r.title}</p>{r.description && <p className="text-xs mt-0.5" style={{ color: "var(--emp-muted)" }}>{r.description}</p>}</td>
                  <td className="p-3 text-sm">{r.employee_name || r.employee_code}</td>
                  <td className="p-3 text-xs">{r.due_date || "—"}</td>
                  <td className="p-3"><span className={`emp-grade emp-grade-${r.status === "done" ? "Gold" : "Silver"}`}>{r.status}</span></td>
                  <td className="p-3"><button onClick={() => remove(r.id)} className="emp-btn-ghost" style={{ padding: "4px 8px", color: "#EF4444", borderColor: "#EF4444" }} data-testid={`task-del-${r.id}`}><Trash2 className="w-3 h-3" strokeWidth={1.5} /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// Shipment B · Weekly CEO Report Panel
// ============================================================
export function CeoReportPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [recipientsInput, setRecipientsInput] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ceo-report/config`, auth());
      setCfg(data);
      setRecipientsInput((data.recipients || []).join(", "));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load config"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = recipientsInput.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
      const { data } = await axios.patch(`${API}/admin/ceo-report/config`, {
        recipients, send_dow: cfg.send_dow, send_time: cfg.send_time, active: cfg.active,
      }, auth());
      setCfg(data);
      toast.success("Weekly CEO schedule saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const preview = () => {
    const url = `${API}/admin/ceo-report/preview`;
    fetch(url, auth()).then(r => r.blob()).then(b => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(b);
      link.download = "TileRoom_CEO_Preview.pdf";
      link.click();
    }).catch(() => toast.error("Preview failed"));
  };

  const sendNow = async () => {
    if (!window.confirm("Send the weekly CEO report to all recipients now?")) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/ceo-report/send-now`, {}, auth());
      toast.success(data.ok ? `Sent to ${data.recipients.length} recipient(s)` : (data.detail || "No recipients"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  const sendTest = async () => {
    if (!testEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/ceo-report/send-test`, { email: testEmail }, auth());
      toast.success(data.ok ? "Test email sent" : (data.detail || "Failed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  if (!cfg) return <p className="text-center py-16 emp-eyebrow">Loading…</p>;

  return (
    <div className="emp-scope emp-fade-in space-y-6">
      <div>
        <p className="emp-eyebrow inline-flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Weekly CEO Report</p>
        <h2 className="font-editorial text-3xl mt-1">ETS Risers & Fallers · every week</h2>
        <p className="text-sm mt-2" style={{ color: "var(--emp-muted)" }}>An automated digest of the top 5 employees whose ETS rose the most and dropped the most over the last 7 days, delivered as a branded PDF to the CEO's inbox.</p>
      </div>

      <div className="emp-glass p-6 space-y-4" data-testid="ceo-config-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="emp-eyebrow block mb-2">Send Day</label>
            <select className="emp-input" value={cfg.send_dow} onChange={e => setCfg({ ...cfg, send_dow: +e.target.value })} data-testid="ceo-dow">
              {DAYS_OF_WEEK.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="emp-eyebrow block mb-2">Send Time (IST)</label>
            <input type="time" className="emp-input" value={cfg.send_time} onChange={e => setCfg({ ...cfg, send_time: e.target.value })} data-testid="ceo-time" />
          </div>
          <div>
            <label className="emp-eyebrow block mb-2">Active</label>
            <button onClick={() => setCfg({ ...cfg, active: !cfg.active })} className="emp-btn-ghost w-full" style={cfg.active ? { color: "#10B981", borderColor: "#10B981" } : {}} data-testid="ceo-active">
              {cfg.active ? "ON · will send" : "OFF · paused"}
            </button>
          </div>
        </div>
        <div>
          <label className="emp-eyebrow block mb-2">Recipients (comma or space separated)</label>
          <textarea rows={2} className="emp-input resize-none" placeholder="ceo@yourbrand.com, cofounder@yourbrand.com" value={recipientsInput} onChange={e => setRecipientsInput(e.target.value)} data-testid="ceo-recipients" />
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <button onClick={save} disabled={saving} className="emp-btn-primary inline-flex items-center gap-2" data-testid="ceo-save">
            {saving ? "Saving…" : "Save Schedule"}
          </button>
          <button onClick={preview} className="emp-btn-ghost inline-flex items-center gap-2" data-testid="ceo-preview">
            <FileDown className="w-3.5 h-3.5" strokeWidth={1.5} /> Preview PDF
          </button>
          <button onClick={sendNow} disabled={sending} className="emp-btn-ghost inline-flex items-center gap-2" style={{ borderColor: "var(--emp-accent)", color: "var(--emp-accent)" }} data-testid="ceo-send-now">
            <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Send Report Now
          </button>
        </div>
        {cfg.last_sent_at && (
          <p className="text-xs" style={{ color: "var(--emp-muted)" }}>
            Last: {new Date(cfg.last_sent_at).toLocaleString()} · {cfg.last_status}
          </p>
        )}
      </div>

      <div className="emp-glass p-6" data-testid="ceo-test-card">
        <p className="emp-eyebrow inline-flex items-center gap-1.5 mb-3"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Send a Test Email</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input className="emp-input flex-1" placeholder="you@yourdomain.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="ceo-test-email" />
          <button onClick={sendTest} disabled={sending} className="emp-btn-primary inline-flex items-center gap-2" data-testid="ceo-test-send">
            <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Send Test
          </button>
        </div>
        <p className="text-xs mt-2" style={{ color: "var(--emp-muted)" }}>Sends the current week's PDF to your inbox without touching CEO recipients.</p>
      </div>
    </div>
  );
}
